/* eslint-disable camelcase */
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { makeStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import Button from '@material-ui/core/Button';
import PropTypes from 'prop-types';
import Divider from '@material-ui/core/Divider';
import CircularProgress from '@material-ui/core/CircularProgress';
import moment from 'moment';

import { useHistory } from 'react-router-dom';
import BatteryInfo from './BatteryInfo';
import DeviceStatus from './DeviceStatus';
import ErrorInfo from './ErrorInfo';
import { unlinkDevice } from '../store/actions/devices';

const useStyles = makeStyles(() => ({
  root: {
    minWidth: 275,
  },
  title: {
    fontSize: 18,
    margin: 0,
    color: '#1e3f9e',
  },
  subtitle: {
    margin: 0,
    fontWeight: 'normal',
    color: '#888',
    fontSize: 12,
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  divider: {
    width: 'calc(100% + 4rem)',
    marginTop: '1rem',
  },
  paper: {
    padding: '2rem',
    paddingTop: '1rem',
    outline: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  contentRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    minWidth: 275,
    marginTop: '.3rem',
  },
  emptyDeviceContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    minWidth: 275,
    margin: 'auto',
  },
  deviceInfoBox: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  linkTextBox: {
    margin: 0,
    fontWeight: 'normal',
    color: '#888',
    fontSize: 12,
    paddingTop: '2rem',
  },
  deviceInfoTitle: {
    fontSize: 14,
    marginRight: 10,
  },
  error: {
    marginTop: '2rem',
    width: '100%',
  },
  buttonRow: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: '2rem',
    width: '100%',
    justifyContent: 'space-around',
  },
  button: {
    color: 'white',
    backgroundColor: '#00d1b2',
    '&:hover': {
      backgroundColor: '#00bca0',
    },
  },
  spinner: {
    padding: 55,
  },
}));

export const DeviceInfo = ({ device, actions }) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  // eslint-disable-next-line camelcase
  const { id, mac_address, battery, last_message_time, offline_message, alert_message, alert_type } = device;

  const handleNavigation = () => {
    history.push(`../monitors/${id}`);
  };

  const handleUnlinkDevice = () => {
    dispatch(unlinkDevice(id, device.patient.id));
  };

  return (
    <Card className={classes.paper}>
      <h2 className={classes.title}>
        Monitor ID:
        {mac_address}
      </h2>
      <div className={classes.contentRow}>
        <h4 className={classes.subtitle}>
          ID:
          {id}
        </h4>
        <h4 className={classes.subtitle}>
          {`Last Update: ${last_message_time ? moment(last_message_time).format('M/DD/YY hh:mm a') : 'N/A'}`}
        </h4>
      </div>
      <Divider className={classes.divider} />
      <div className={classes.contentRow}>
        <div className={classes.deviceInfoBox}>
          <h3 className={classes.deviceInfoTitle}>Status:</h3>
          <DeviceStatus status={offline_message ? 3 : 2} />
        </div>
        <div className={classes.deviceInfoBox}>
          <h3 className={classes.deviceInfoTitle}>Battery:</h3>
          <BatteryInfo battery={battery} showText />
        </div>
      </div>
      {alert_type && (
        <div className={classes.error}>
          <ErrorInfo status={alert_type} errorText={alert_message} showText />
        </div>
      )}
      {actions && (
        <div className={classes.buttonRow}>
          <Button className={classes.button} size="small" variant="contained" onClick={handleNavigation}>
            View Monitor
          </Button>
          <Button className={classes.button} size="small" variant="contained" onClick={handleUnlinkDevice}>
            Unlink Monitor
          </Button>
        </div>
      )}
    </Card>
  );
};

export const EmptyDeviceInfo = ({ openLinkDevice, patient, index }) => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!patient) setIsLoading(false);
    const numOfDevices = (patient.device_a ? 1 : 0) + (patient.device_b ? 1 : 0);
    if (index < numOfDevices) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, []);

  return (
    <Card className={classes.paper}>
      <h2 className={classes.title}>{isLoading ? '' : 'No Monitor Connected'}</h2>
      <Divider className={classes.divider} />
      <div className={classes.emptyDeviceContent}>
        {isLoading && (
          <div className={classes.spinner}>
            <CircularProgress />
          </div>
        )}
        {!isLoading && (
          <h3 className={classes.linkTextBox}>
            Each patient should be
            <br />
            paired with two monitors
          </h3>
        )}
      </div>
      {!isLoading && (
        <div className={classes.buttonRow}>
          <Button className={classes.button} size="small" variant="contained" onClick={openLinkDevice}>
            Link Monitor
          </Button>
        </div>
      )}
    </Card>
  );
};

DeviceInfo.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  device: PropTypes.any.isRequired,
};

EmptyDeviceInfo.propTypes = {
  openLinkDevice: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  patient: PropTypes.any,
  index: PropTypes.number.isRequired,
};
EmptyDeviceInfo.defaultProps = {
  patient: null,
};
